import Link from 'next/link'
import classes from 'classnames'
import Thumbnail from 'components/Thumbnail'
import Button from 'components/Button'
import {
  faCheck,
  faHourglass,
  faLayerGroup
} from '@fortawesome/pro-regular-svg-icons'
import { useCollectiveContext } from 'context/CollectiveContext'
import { useSignProposal } from 'hooks/collectives/useSignProposal'
import {
  CollectiveProposalQueryResult,
  CollectiveProposalStatus,
  CollectiveSignatorsQueryResult,
  useCollectiveSignatorsQuery,
  useProposalChangedSubscription
} from 'graphql/generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useExecuteProposal } from 'hooks/collectives/useExecuteProposal'
import { TTxUiState } from 'lib/collectives/helpers'
import { useWeb3 } from 'context/Web3Context'
import { noop } from 'lodash'
import StyledTooltip from 'components/Tooltip/Tooltip'
import {
  DemoTooltipText,
  useCollectiveDemoContext
} from 'context/CollectiveDemoContext'
import { motion } from 'framer-motion'
import { useSignProposal as useDemoSignProposal } from 'hooks/collectives/demo/useSignProposal'
import { useExecuteProposal as useDemoExecuteProposal } from 'hooks/collectives/demo/useExecuteProposal'
import ModalTrigger from '../../common/ModalTrigger'
import { BatchProposalsModal } from '../modals/BatchProposalsModal'
import WalletCheckInfo from '../Common/WalletCheckInfo'
import { useWalletCheck } from 'hooks/collectives/useWalletCheck'

type TProposal = NonNullable<
  CollectiveProposalQueryResult['collectiveProposal']
>
interface IProps {
  proposal: TProposal
}

function executeLabelForState(state: Maybe<TTxUiState>) {
  switch (state) {
    case TTxUiState.WAITING_FOR_PROVIDER:
      return 'Waiting for Wallet...'
    case TTxUiState.WAITING_FOR_NETWORK:
      return 'Waiting for Confirmations...'
    case TTxUiState.TX_SUCCESS:
      return 'Executed Successfully'
    case TTxUiState.TX_FAILURE:
      return 'Failed to Execute'
    default:
      return 'Sign & Execute Proposal'
  }
}

function isExecuting(state: Maybe<TTxUiState>) {
  switch (state) {
    case TTxUiState.WAITING_FOR_PROVIDER:
      return true
    case TTxUiState.WAITING_FOR_NETWORK:
      return true
    default:
      return false
  }
}

function Signator({
  signator,
  signed
}: {
  signator: NonNullable<
    CollectiveSignatorsQueryResult['collective']
  >['signators'][0]
  signed: boolean
}) {
  return (
    <div
      key={`signator-${signator.id}`}
      className="flex flex-col items-center mb-2"
    >
      <div className="relative">
        <Thumbnail
          src={signator.user.platformUser?.thumb}
          className={classes(
            'h-12 w-12 rounded-full overflow-hidden',
            !signed && 'opacity-50'
          )}
        />

        <div
          className={classes(
            'absolute top-0 -right-2 z-50  text-white text-xs flex justify-center items-center w-5 h-5 rounded-full',
            signed ? 'bg-green-500' : 'bg-blue'
          )}
        >
          <FontAwesomeIcon icon={signed ? faCheck : faHourglass} size="sm" />
        </div>
      </div>

      <p className="text-center text-sm font-light text-text-primary pt-1 w-24 truncate">
        {signator.user.platformUser
          ? signator.user.platformUser.displayName
          : signator.user.address}
      </p>
    </div>
  )
}

function BatchNotice({ proposal }: IProps) {
  return (
    <div className="border-t border-gray-200 mt-4 pt-4 space-y-4">
      <p className="text-sm font-light text-text-primary">
        {`This proposal has passed voting, and is ready to be executed. It has
        been rolled-up into a batch execution proposal in order to reduce gas
        fees.`}
        {!!proposal.batchProposal && (
          <>
            {` In order to execute it, `}
            <Link className="text-blue" href={proposal.batchProposal.publicUrl}>
              {`head over to the batch proposal page`}
            </Link>
            {`.`}
          </>
        )}
      </p>
    </div>
  )
}

function ProposalExecutionControls({ proposal }: IProps) {
  const { collective, demoMode } = useCollectiveContext()

  const { currentUser, demoActiveProposal } = useCollectiveDemoContext()

  const { signerAddress } = useWeb3()

  const { signProposal } = useSignProposal({ collective, proposal })

  const demoSignProposal = useDemoSignProposal()
  const demoExecuteProposal = useDemoExecuteProposal()

  const { showWalletNotice } = useWalletCheck(collective)

  const { executeProposal, state: executionState } = useExecuteProposal({
    collective,
    proposal
  })

  const { data, refetch } = useCollectiveSignatorsQuery({
    variables: {
      collectiveId: collective.id
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: demoMode
  })

  const signators = data?.collective?.signators || []
  const signatorsWhoSigned = signators.filter(signator =>
    proposal.signatures.some(x => x.user.id === signator.user.id && x.valid)
  )

  const signatorsWhosSignaturesAreInvalid = signators.filter(signator =>
    proposal.signatures.some(x => x.user.id === signator.user.id && !x.valid)
  )
  const signatorsWhoHaventSigned = signators.filter(
    signator => !proposal.signatures.some(x => x.user.id === signator.user.id)
  )

  const pendingSignatures = [
    ...signatorsWhoHaventSigned,
    ...signatorsWhosSignaturesAreInvalid
  ]

  useProposalChangedSubscription({
    variables: {
      proposalId: proposal.id
    },
    skip: demoMode,
    onData: () => refetch()
  })

  if (proposal.status !== CollectiveProposalStatus.SUCCEEDED) {
    return null
  } else if (proposal.isPartOfBatch) {
    return <BatchNotice proposal={proposal} />
  }

  const isSignator = collective.membership?.privileged || false
  const viewerAlreadySigned = proposal.signatures.some(
    x =>
      signerAddress &&
      x.user.address.toLowerCase() === signerAddress.toLowerCase() &&
      x.valid
  )
  const viewersSignatureWasInvalidated = proposal.signatures.some(
    x =>
      signerAddress &&
      x.user.address.toLowerCase() === signerAddress.toLowerCase() &&
      !x.valid
  )
  const awaitingMoreSignatures =
    collective.safeThreshold >
    proposal.signatures.filter(x => x.valid).length + 1

  return (
    <div className="border-t border-gray-200 mt-4 pt-4 space-y-4">
      <p className="text-sm font-light text-text-primary">
        {`This proposal passed! It’s now ready to be executed. Once executed, it’ll be recorded on the blockchain. At minimum, ${collective.safeThreshold} signators are required to execute a proposal. So far, we've collected ${proposal.signatures.length} signatures.`}
      </p>

      <div className="bg-gray-100 border border-gray-300 rounded-lg flex flex-col space-x-2">
        {signatorsWhoSigned.length > 0 && (
          <div className="p-2 flex flex-col gap-2">
            <p className="text-xs font-light text-text-secondary pb-3">
              Signed
            </p>

            <div className={'flex flex-wrap overflow-auto'}>
              {signatorsWhoSigned.map(signator => (
                <Signator
                  key={`signator-${signator.id}`}
                  signator={signator}
                  signed={proposal.signatures
                    .filter(x => x.valid)
                    .some(x => x.user.id === signator.user.id)}
                />
              ))}
            </div>
          </div>
        )}

        {pendingSignatures.length > 0 && (
          <div className="p-2 flex flex-col gap-2">
            <p className="text-xs font-light text-text-secondary pb-3">
              Awaiting Signature
            </p>

            <div className={'flex flex-wrap overflow-auto'}>
              {pendingSignatures.map(signator => (
                <Signator
                  key={`signator-${signator.id}`}
                  signator={signator}
                  signed={proposal.signatures
                    .filter(x => x.valid)
                    .some(x => x.user.id === signator.user.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {isSignator && (
        <div className="flex flex-col items-center space-y-1">
          <div className="flex flex-row justify-center space-x-2">
            <motion.div
              animate={
                demoMode &&
                proposal.id === demoActiveProposal?.id &&
                proposal.myVote
                  ? {
                      scale: [1, 1.15, 1]
                    }
                  : undefined
              }
              transition={
                demoMode &&
                proposal.id === demoActiveProposal?.id &&
                proposal.myVote
                  ? {
                      type: 'spring',
                      damping: 10,
                      stiffness: 70,
                      duration: 1.5,
                      repeat: Infinity
                    }
                  : undefined
              }
            >
              {awaitingMoreSignatures ? (
                <Button
                  label={'Sign Proposal'}
                  icon={faCheck}
                  color={'green'}
                  onClick={() =>
                    demoMode
                      ? demoSignProposal(proposal.id, currentUser)
                      : signProposal()
                  }
                  disabled={viewerAlreadySigned || showWalletNotice}
                />
              ) : (
                <Button
                  label={executeLabelForState(executionState)}
                  icon={faCheck}
                  color={'green'}
                  onClick={() =>
                    demoMode
                      ? demoExecuteProposal(proposal.id, currentUser)
                      : executeProposal()
                  }
                  loading={isExecuting(executionState)}
                  disabled={
                    isExecuting(executionState) ||
                    viewerAlreadySigned ||
                    (showWalletNotice && !demoMode)
                  }
                />
              )}
            </motion.div>

            {!proposal.isBatch && (
              <ModalTrigger
                trigger={onClick => (
                  <StyledTooltip arrow title={demoMode ? DemoTooltipText : ''}>
                    <Button
                      label={`Batch Proposal`}
                      icon={faLayerGroup}
                      color={'gray'}
                      onClick={demoMode ? noop : onClick}
                      disabled={showWalletNotice}
                    />
                  </StyledTooltip>
                )}
              >
                <BatchProposalsModal />
              </ModalTrigger>
            )}
          </div>

          {!demoMode && (
            <div className="pt-4">
              <WalletCheckInfo
                collective={collective}
                className="bg-red-50 hover:bg-red-100 px-5 py-3 rounded-lg"
              />
            </div>
          )}

          {viewerAlreadySigned && (
            <p className="font-medium text-center text-gray-700 text-xs pt-1">{`You've already signed this proposal.`}</p>
          )}

          {viewersSignatureWasInvalidated && (
            <p className="font-medium text-center text-gray-700 text-xs pt-1">{`You need to re-sign this proposal. Your earlier signature was invalidated due to out-of-order execution. You can learn more about that by clicking here.`}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default ProposalExecutionControls
